<template>
  <!-- Status indicator with toggle between list and single mode -->
  <div v-if="visible" class="status-container mt-2" :class="{ 'is-completed': isCompleted }">
    <!-- List mode - show all items -->
    <div v-if="showList" class="status-list-wrapper">
      <TransitionGroup name="status-item" tag="div" class="status-list">
        <div 
          v-for="item in statusList" 
          :key="item.id" 
          class="status-item"
          :class="{ 'is-latest': item.id === latestId }"
        >
          <div class="status-item-left">
            <v-icon 
                size="14" 
                class="status-icon mr-1"
                :class="{ 'rotating': item.status === 0 && item.id === latestId && !isCompleted }"
                :color="getIconColor(item.status)"
            >
                {{ getIcon(item.status) }}
            </v-icon>
            <span class="status-text">{{ item.message }}</span>
          </div>
          <div class="status-item-right">
            <!-- แสดง timer ต่อท้าย item ล่าสุด -->
            <span v-if="item.id === latestId" class="timer">{{ formattedTime }}</span>
            <span v-else-if="item.duration" class="item-duration">{{ item.duration }}s</span>
            <!-- Toggle button ต่อท้าย item ล่าสุด -->
            <v-btn
                v-if="item.id === latestId"
                icon
                size="14"
                variant="text"
                @click="showList = !showList"
                class="toggle-btn ml-1"
                title="ซ่อนรายละเอียด"
            >
                <v-icon size="14">mdi-chevron-up</v-icon>
            </v-btn>
          </div>
        </div>
      </TransitionGroup>
    </div>

    <!-- Single mode - show only latest + timer + toggle -->
    <div v-else class="status-single-row">
      <div class="status-single">
        <v-icon 
          size="14" 
          class="status-icon"
          :class="{ 'rotating': latestItem && latestItem.status === 0 && !isCompleted }"
          :color="latestItem ? getIconColor(latestItem.status) : '#0078D4'"
        >
          {{ latestItem ? getIcon(latestItem.status) : 'mdi-loading' }}
        </v-icon>
        <span class="status-text">{{ latestItem ? latestItem.message : '' }}</span>
      </div>
      <div class="status-single">
        <span class="timer">{{ formattedTime }}</span>
        <v-btn
            icon
            size="14"
            variant="text"
            @click="showList = !showList"
            class="toggle-btn ml-1"
            title="แสดงรายละเอียด"
        >
            <v-icon size="14">mdi-chevron-down</v-icon>
        </v-btn>
      </div>
    </div>
  </div>
</template>

<script>
import statusService from '@/services/status-service';

// Status types matching backend enum
const StatusType = {
  Processing: 0,
  Completed: 1,
  Error: 2,
  Done: 3  // เสร็จแค่ step นี้ (ติ๊กถูก แต่ไม่ clear list)
};

export default {
  name: 'StatusIndicator',
  
  props: {
    // User ID for SignalR group
    userId: {
      type: String,
      required: true
    },
    // Session ID (KeyTopic) for filtering - only show status for this session
    sessionId: {
      type: String,
      default: null
    },
    // Auto-connect when mounted
    autoConnect: {
      type: Boolean,
      default: true
    },
    // Default message when processing starts
    defaultMessage: {
      type: String,
      default: 'กำลังประมวลผล...'
    },
    // Maximum items to show in list
    maxItems: {
      type: Number,
      default: 10
    },
    // Initial status list (for passing from another StatusIndicator)
    initialStatusList: {
      type: Array,
      default: () => []
    },
    // Initial completed state
    initialCompleted: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      statusList: [],
      isProcessing: false,
      isConnected: false,
      handlerId: `status-${Date.now()}`,
      itemIdCounter: 0,
      showList: true,  // toggle: true = แสดง list, false = แสดงแค่ตัวล่าสุด (default กางเลย)
      isCompleted: false,  // flag ว่าจบแล้วหรือยัง
      startTime: null,  // เวลาเริ่มต้นของ item ล่าสุด
      elapsedMs: 0,  // เวลาที่ผ่านไป (มิลลิวินาที)
      timerInterval: null  // interval สำหรับ update timer
    };
  },

  computed: {
    visible() {
      return this.statusList.length > 0;
    },
    
    latestId() {
      if (this.statusList.length === 0) return null;
      return this.statusList[this.statusList.length - 1].id;
    },
    
    latestItem() {
      if (this.statusList.length === 0) return null;
      return this.statusList[this.statusList.length - 1];
    },
    
    formattedTime() {
      const seconds = this.elapsedMs / 1000;
      if (seconds < 60) {
        return `${seconds.toFixed(1)}s`;
      }
      const mins = Math.floor(seconds / 60);
      const secs = (seconds % 60).toFixed(1);
      return `${mins}m ${secs}s`;
    }
  },

  watch: {
    userId: {
      async handler(newVal, oldVal) {
        if (oldVal && this.isConnected) {
          await statusService.leaveUserGroup(oldVal);
        }
        if (newVal && this.isConnected) {
          await statusService.joinUserGroup(newVal);
        }
      }
    }
  },

  async mounted() {
    // Initialize from props if provided
    if (this.initialStatusList && this.initialStatusList.length > 0) {
      this.statusList = [...this.initialStatusList];
      this.itemIdCounter = Math.max(...this.statusList.map(s => s.id), 0);
    }
    if (this.initialCompleted) {
      this.isCompleted = true;
      // Mark all Processing items as Done when initializing as completed
      // This handles race condition where Completed arrives after StatusIndicator unmount
      this.statusList.forEach(item => {
        if (item.status === 0) { // StatusType.Processing
          item.status = 3; // StatusType.Done
        }
      });
    }
    
    if (this.autoConnect) {
      await this.connect();
    }
  },

  beforeUnmount() {
    this.cleanup();
  },

  methods: {
    async connect() {
      try {
        await statusService.connect();
        this.isConnected = true;

        if (this.userId) {
          await statusService.joinUserGroup(this.userId);
        }

        // Register handler
        statusService.onStatusUpdate(this.handlerId, (status) => {
          this.handleStatusUpdate(status);
        });

      } catch (error) {
        console.error('[StatusIndicator] Connection error:', error);
        this.isConnected = false;
      }
    },

    handleStatusUpdate(status) {
      // Filter by session if specified
      if (this.sessionId && status.sessionId !== this.sessionId) {
        return;
      }

      // Start timer on first status, or restart for each new status
      if (this.statusList.length === 0) {
        this.startTimer();
        this.isCompleted = false;
        this.showList = true; // กางอัตโนมัติเมื่อเริ่ม process ใหม่
      } else {
        // Restart timer for new item (to show duration of latest item)
        this.startTime = Date.now();
        this.elapsedMs = 0;
      }

      // Calculate duration for previous item
      const now = Date.now();
      if (this.statusList.length > 0) {
        const lastItem = this.statusList[this.statusList.length - 1];
        if (lastItem.startMs) {
          lastItem.duration = ((now - lastItem.startMs) / 1000).toFixed(1);
        }
      }

      // Mark previous items as completed before adding new one
      if (status.status === StatusType.Processing) {
        this.statusList.forEach(item => {
          if (item.status === StatusType.Processing) {
            item.status = StatusType.Done;
          }
        });
      }

      // Add to list
      this.itemIdCounter++;
      const newItem = {
        id: this.itemIdCounter,
        message: status.message,
        status: status.status,
        timestamp: status.timestamp,
        startMs: now,
        duration: null
      };
      
      this.statusList.push(newItem);
      
      // Trim if exceeds max
      if (this.statusList.length > this.maxItems) {
        this.statusList.shift();
      }

      if (status.status === StatusType.Processing) {
        this.isProcessing = true;
      } else if (status.status === StatusType.Done) {
        // Done - แค่เป็นติ๊ก ไม่ clear list
        this.isProcessing = true; // ยังไม่จบ
      } else {
        // Completed or Error - mark all and stop timer, collapse but don't clear
        this.statusList.forEach(item => {
          if (item.status === StatusType.Processing) {
            item.status = StatusType.Done;
          }
        });
        // Calculate duration for last item
        const lastItem = this.statusList[this.statusList.length - 1];
        if (lastItem && lastItem.startMs) {
          lastItem.duration = ((now - lastItem.startMs) / 1000).toFixed(1);
        }
        
        this.isProcessing = false;
        this.isCompleted = true;
        this.stopTimer();
        this.showList = false; // collapse เมื่อเสร็จ
      }

      // Emit พร้อม statusList และ isCompleted เพื่อให้ parent เก็บไว้
      this.$emit('status-update', {
        ...status,
        statusList: [...this.statusList],
        isCompleted: this.isCompleted
      });
    },

    startTimer() {
      this.startTime = Date.now();
      this.elapsedMs = 0;
      this.stopTimer(); // clear existing interval
      this.timerInterval = setInterval(() => {
        this.elapsedMs = Date.now() - this.startTime;
      }, 100);
    },

    stopTimer() {
      if (this.timerInterval) {
        clearInterval(this.timerInterval);
        this.timerInterval = null;
      }
    },

    // Get icon based on status
    getIcon(status) {
      if (status === StatusType.Error) return 'mdi-alert';
      if (status === StatusType.Completed) return 'mdi-check';
      if (status === StatusType.Done) return 'mdi-check';
      return 'mdi-loading';
    },

    // Get icon color based on status
    getIconColor(status) {
      if (status === StatusType.Error) return '#dc3545';
      if (status === StatusType.Completed) return '#198754';
      if (status === StatusType.Done) return '#198754';
      return '#0078D4';
    },

    // Manual start - for when chat starts
    start(message = null) {
      this.statusList = [];
      this.isProcessing = true;
      this.isCompleted = false;
      this.startTimer();
      if (message) {
        this.itemIdCounter++;
        this.statusList.push({
          id: this.itemIdCounter,
          message: message,
          status: StatusType.Processing,
          timestamp: new Date().toISOString(),
          startMs: Date.now(),
          duration: null
        });
      }
    },

    // Manual stop - clear list
    stop() {
      this.isProcessing = false;
      this.isCompleted = false;
      this.statusList = [];
      this.stopTimer();
    },

    // Clear history (for new chat)
    clear() {
      this.statusList = [];
      this.isCompleted = false;
      this.elapsedMs = 0;
      this.stopTimer();
    },

    cleanup() {
      this.stopTimer();
      statusService.offStatusUpdate(this.handlerId);
      if (this.userId && this.isConnected) {
        statusService.leaveUserGroup(this.userId);
      }
    }
  }
};
</script>

<style scoped>
.status-container {
  display: inline-flex;
  flex-direction: column;
  padding: 8px 12px;
  background: #f8f9fa;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  min-width: 80px;
  max-width: 350px;
  opacity: 0.85;
  transition: opacity 0.2s ease, box-shadow 0.2s ease;
}

.status-container:hover {
  opacity: 1;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
}

.status-container.is-completed {
  opacity: 0.5;
}

.status-container.is-completed:hover {
  opacity: 0.9;
}

.status-header {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 6px;
  margin-bottom: 2px;
}

.timer {
  font-size: 11px;
  color: #999;
  font-family: monospace;
  min-width: 38px;
  text-align: right;
  display: inline-block;
}

.toggle-btn {
  opacity: 0.5;
}

.toggle-btn:hover {
  opacity: 1;
}

.status-list-wrapper {
  max-height: 200px;
  overflow-y: auto;
}

.status-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.status-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
  font-size: 12px;
  color: #555;
  transition: color 0.3s ease, opacity 0.3s ease;
}

.status-item.is-latest {
  color: #333;
}

.status-item-left {
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
  overflow: hidden;
}

.status-item-right {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  min-width: 60px;
  justify-content: flex-end;
}

.item-duration {
  font-size: 10px;
  color: #777;
  margin-left: auto;
  font-family: monospace;
  min-width: 32px;
  text-align: right;
  display: inline-block;
}

.status-single {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #444;
}

.status-single-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.status-toggle-row {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 6px;
  margin-top: 4px;
  padding-top: 4px;
  border-top: 1px solid #eee;
}

.status-icon {
  flex-shrink: 0;
  opacity: 0.8;
}

.status-text {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.rotating {
  animation: rotate 1s linear infinite;
}

/* Transition animations */
.status-item-enter-active {
  transition: all 0.3s ease-out;
}

.status-item-leave-active {
  transition: all 0.2s ease-in;
}

.status-item-enter-from {
  opacity: 0;
  transform: translateX(-10px);
}

.status-item-leave-to {
  opacity: 0;
  transform: translateX(10px);
}

@keyframes rotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
